import type { ReactNode } from 'react';
import React, { createContext, useMemo } from 'react';
import type { ErrorState } from '@makemydeal/dr-dash-ui-utils';

export type LenderAddressFormErrors = Record<string, ErrorState | undefined>;

export type LenderAddressValidationContextValue = {
    getFieldError: (field: string) => ErrorState | undefined;
    onFieldValidation: (field: string, value: string) => void;
};

export const LenderAddressValidationContext = createContext<LenderAddressValidationContextValue | undefined>(undefined);

type LenderAddressValidationProviderProps = {
    children: ReactNode;
    // Nested error tree from the lender validation engine, keyed by field path.
    formErrors: LenderAddressFormErrors;
    // Validates the changed field's value against the engine (also drives the lender red dot).
    onAddressFieldValidation: (field: string, value: string) => void;
};

/**
 * Shares the lender validation engine's per-field error state and change handler with the titling
 * address fields via context, so `BaseDealLender` no longer drills `error`/`onFieldValidation`
 * props through each field wrapper (mirrors CustomerAddressProvider).
 */
export const LenderAddressValidationProvider = ({
    children,
    formErrors,
    onAddressFieldValidation
}: LenderAddressValidationProviderProps) => {
    const value = useMemo<LenderAddressValidationContextValue>(
        () => ({
            getFieldError: (field: string) => formErrors[field],
            onFieldValidation: onAddressFieldValidation
        }),
        [formErrors, onAddressFieldValidation]
    );

    return <LenderAddressValidationContext.Provider value={value}>{children}</LenderAddressValidationContext.Provider>;
};
